// ═══════════════════════════════════════════════════════════════════════════════
// METRICS ENGINE — pure functions, no React, no side effects
// Uses MediaPipe FaceLandmarker 468-point indices
// ═══════════════════════════════════════════════════════════════════════════════

// ── MediaPipe 468-point landmark indices ────────────────────────────────────
// Eye landmarks (from MediaPipe Face Mesh canonical map)
export const MP = {
  // Left eye (from subject's perspective = right on screen)
  LEFT_EYE:  { outer:33,  inner:133, top:[160,159,158], bot:[144,145,153], all:[33,7,163,144,145,153,154,155,133,173,157,158,159,160,161,246] },
  // Right eye
  RIGHT_EYE: { outer:362, inner:263, top:[387,386,385], bot:[373,374,380], all:[362,382,381,380,374,373,390,249,263,466,388,387,386,385,384,398] },
  // Iris (MediaPipe iris landmarks — points 468-477, only available with iris model)
  // Without iris model, we use eye corners for gaze proxy
  LEFT_IRIS:  [468,469,470,471,472],
  RIGHT_IRIS: [473,474,475,476,477],
  // Brow
  LEFT_BROW:  [70,63,105,66,107,55,65,52,53,46],
  RIGHT_BROW: [300,293,334,296,336,285,295,282,283,276],
  // Mouth outer
  MOUTH_OUTER: [61,185,40,39,37,0,267,269,270,409,291,375,321,405,314,17,84,181,91,146],
  // Mouth inner
  MOUTH_INNER: [78,191,80,81,82,13,312,311,310,415,308,324,318,402,317,14,87,178,88,95],
  // Lip corners
  MOUTH_LEFT:  61,
  MOUTH_RIGHT: 291,
  MOUTH_TOP:   13,
  MOUTH_BOT:   14,
  // Nose
  NOSE_TIP:  4,
  NOSE_BASE: 2,
  LEFT_NARIS:  49,
  RIGHT_NARIS: 279,
  // Chin
  CHIN: 152,
  // Jawline
  JAW: [234,93,132,58,172,136,150,149,176,148,152,377,400,378,379,365,397,288,361,323,454],
  // Face outline for normalisation
  FACE_TOP:    10,
  FACE_BOT:   152,
  FACE_LEFT:  234,
  FACE_RIGHT: 454,
  // Cheekbones (for jaw clench proxy)
  CHEEK_LEFT:  234,
  CHEEK_RIGHT: 454,
  JAW_LEFT:    172,
  JAW_RIGHT:   397,
};

const dist  = (a, b) => Math.hypot((a.x-b.x), (a.y-b.y));
const avg   = arr => arr.reduce((s,v) => s+v, 0) / arr.length;
const avgPt = (lm, idxs) => ({
  x: avg(idxs.map(i => lm[i].x)),
  y: avg(idxs.map(i => lm[i].y)),
});

// ── Face normalisation scalar ────────────────────────────────────────────────
export function faceScale(lm) {
  return dist(lm[MP.FACE_TOP], lm[MP.FACE_BOT]) || 1;
}

// ── EAR — Eye Aspect Ratio ───────────────────────────────────────────────────
// Uses 6 MediaPipe eye points per eye
function earForEye(lm, eye) {
  const { outer, inner, top, bot } = eye;
  const A = dist(avgPt(lm, top), avgPt(lm, bot));
  const C = dist(lm[outer], lm[inner]);
  return C > 0 ? A / C : 0;
}
export function calcEAR(lm) {
  const left  = earForEye(lm, MP.LEFT_EYE);
  const right = earForEye(lm, MP.RIGHT_EYE);
  return { left, right, avg: (left + right) / 2 };
}

// ── MAR — Mouth Aspect Ratio (yawn) ─────────────────────────────────────────
export function calcMAR(lm) {
  const w = dist(lm[MP.MOUTH_LEFT], lm[MP.MOUTH_RIGHT]);
  if (w < 0.001) return 0;
  const h = dist(lm[MP.MOUTH_TOP], lm[MP.MOUTH_BOT]);
  return h / w;
}

// ── Gaze direction ────────────────────────────────────────────────────────────
// Without iris model: uses eye corner midpoint vs eye centre as proxy
// With iris landmarks (if available): uses iris centroid
export function calcGaze(lm) {
  const hasIris = lm.length > 468;

  if (hasIris) {
    // True iris-based gaze
    const leftIris  = avgPt(lm, MP.LEFT_IRIS);
    const rightIris = avgPt(lm, MP.RIGHT_IRIS);
    const leftEye   = { outer: lm[MP.LEFT_EYE.outer],  inner: lm[MP.LEFT_EYE.inner]  };
    const rightEye  = { outer: lm[MP.RIGHT_EYE.outer], inner: lm[MP.RIGHT_EYE.inner] };

    const leftW   = dist(leftEye.outer, leftEye.inner);
    const rightW  = dist(rightEye.outer, rightEye.inner);
    const leftH   = avg([...MP.LEFT_EYE.top,  ...MP.LEFT_EYE.bot ].map(i => lm[i].y));
    const rightH  = avg([...MP.RIGHT_EYE.top, ...MP.RIGHT_EYE.bot].map(i => lm[i].y));
    const leftMidX  = (leftEye.outer.x  + leftEye.inner.x)  / 2;
    const rightMidX = (rightEye.outer.x + rightEye.inner.x) / 2;
    const leftMidY  = avg([...MP.LEFT_EYE.top,  ...MP.LEFT_EYE.bot].map(i => lm[i].y));
    const rightMidY = avg([...MP.RIGHT_EYE.top, ...MP.RIGHT_EYE.bot].map(i => lm[i].y));

    const lGH = leftW  > 0 ? (leftIris.x  - leftMidX)  / (leftW  / 2) : 0;
    const rGH = rightW > 0 ? (rightIris.x - rightMidX) / (rightW / 2) : 0;
    const lGV = (leftIris.y  - leftMidY)  * 10;
    const rGV = (rightIris.y - rightMidY) * 10;

    return {
      horizontal: (lGH + rGH) / 2,
      vertical:   (lGV + rGV) / 2,
      gazeAsym:   Math.abs(lGH - rGH),
      hasIris: true,
    };
  }

  // Fallback: use eye geometry without iris
  const lOuter = lm[MP.LEFT_EYE.outer],  lInner = lm[MP.LEFT_EYE.inner];
  const rOuter = lm[MP.RIGHT_EYE.outer], rInner = lm[MP.RIGHT_EYE.inner];
  const lTopY  = avg(MP.LEFT_EYE.top.map(i  => lm[i].y));
  const lBotY  = avg(MP.LEFT_EYE.bot.map(i  => lm[i].y));
  const rTopY  = avg(MP.RIGHT_EYE.top.map(i => lm[i].y));
  const rBotY  = avg(MP.RIGHT_EYE.bot.map(i => lm[i].y));
  // Approximate iris as midpoint of vertical extent
  const lIrisX = avg(MP.LEFT_EYE.top.map(i  => lm[i].x));
  const rIrisX = avg(MP.RIGHT_EYE.top.map(i => lm[i].x));
  const lIrisY = (lTopY + lBotY) / 2;
  const rIrisY = (rTopY + rBotY) / 2;
  const lW = dist(lOuter, lInner), rW = dist(rOuter, rInner);
  const lH = lm[MP.LEFT_EYE.outer].y, rH = lm[MP.RIGHT_EYE.outer].y;

  return {
    horizontal: lW > 0 ? ((lIrisX - (lOuter.x + lInner.x)/2) / (lW/2) + (rIrisX - (rOuter.x + rInner.x)/2) / (rW/2)) / 2 : 0,
    vertical:   0, // unreliable without iris
    gazeAsym:   0,
    hasIris: false,
  };
}

// ── Head pose (yaw/pitch) ────────────────────────────────────────────────────
// Uses face symmetry: nose tip vs midpoint of ear-equivalent points
export function calcHeadPose(lm) {
  const noseTip  = lm[MP.NOSE_TIP];
  const faceLeft = lm[MP.FACE_LEFT];
  const faceRight = lm[MP.FACE_RIGHT];
  const faceTop  = lm[MP.FACE_TOP];
  const faceBot  = lm[MP.FACE_BOT];

  const faceMidX = (faceLeft.x + faceRight.x) / 2;
  const faceMidY = (faceTop.y  + faceBot.y)  / 2;
  const faceW    = dist(faceLeft, faceRight) || 1;
  const faceH    = dist(faceTop,  faceBot)   || 1;

  return {
    yaw:   (noseTip.x - faceMidX) / (faceW / 2),   // -1=turned left, 0=forward, 1=right
    pitch: (noseTip.y - faceMidY) / (faceH / 2),   // -1=looking up, 0=forward, 1=down
  };
}

// ── Brow metrics ─────────────────────────────────────────────────────────────
export function calcBrowMetrics(lm) {
  const scale = faceScale(lm);
  const leftBrowY  = avg(MP.LEFT_BROW.map(i  => lm[i].y));
  const rightBrowY = avg(MP.RIGHT_BROW.map(i => lm[i].y));
  const leftEyeY   = avg(MP.LEFT_EYE.all.map(i  => lm[i].y));
  const rightEyeY  = avg(MP.RIGHT_EYE.all.map(i => lm[i].y));

  const leftLift   = (leftEyeY  - leftBrowY)  / scale;
  const rightLift  = (rightEyeY - rightBrowY) / scale;
  const browLift   = (leftLift + rightLift) / 2;
  const browAsym   = Math.abs(leftLift - rightLift);
  // Inner brow convergence — how close inner brow corners are
  const innerL = lm[MP.LEFT_BROW[0]],  innerR = lm[MP.RIGHT_BROW[0]];
  const innerBrowDist = dist(innerL, innerR) / scale;

  return { browLift, browAsym, innerBrowDist, leftLift, rightLift };
}

// ── Lip metrics ──────────────────────────────────────────────────────────────
export function calcLipMetrics(lm) {
  const scale = faceScale(lm);
  const left  = lm[MP.MOUTH_LEFT];
  const right = lm[MP.MOUTH_RIGHT];
  const top   = lm[MP.MOUTH_TOP];
  const bot   = lm[MP.MOUTH_BOT];
  const midY  = (top.y + bot.y) / 2;

  const leftLift  = (midY - left.y) / scale;
  const rightLift = (midY - right.y) / scale;
  const smileScore    = (leftLift + rightLift) / 2;
  const lipAsym       = Math.abs(leftLift - rightLift);
  const mouthW        = dist(left, right);
  const innerLipH     = mouthW > 0 ? dist(top, bot) / mouthW : 0;
  const upperLipCurl  = mouthW > 0 ? ((left.y + right.y)/2 - top.y) / scale : 0;

  return { smileScore, lipAsym, innerLipH, upperLipCurl, leftLift, rightLift };
}

// ── Jaw metrics ──────────────────────────────────────────────────────────────
export function calcJawMetrics(lm) {
  const scale = faceScale(lm);
  const cheekW = dist(lm[MP.CHEEK_LEFT], lm[MP.CHEEK_RIGHT]) / scale;
  const jawW   = dist(lm[MP.JAW_LEFT],  lm[MP.JAW_RIGHT])   / scale;
  return {
    jawClenchRatio: cheekW / (jawW + 0.001),
    cheekW, jawW,
  };
}

// ── Chin metrics ─────────────────────────────────────────────────────────────
export function calcChinMetrics(lm) {
  const scale = faceScale(lm);
  return { chinToNose: dist(lm[MP.CHIN], lm[MP.NOSE_BASE]) / scale };
}

// ── Attention score (0–100) ──────────────────────────────────────────────────
// Weighted combination of gaze, blink rate, PERCLOS, head orientation, face presence
export function calcAttentionScore({ gazeOnScreen, blinkRate, perclos, headYaw, headPitch, facePresent, blinkDurationAvg }) {
  if (!facePresent) return 0;

  // Each factor 0–100, higher = more attentive
  const gazeScore    = gazeOnScreen ? 100 : 30;
  const blinkScore   = blinkRate >= 5 && blinkRate <= 20 ? 100 : blinkRate < 5 ? 60 : 50; // too few = zoned out, too many = stressed
  const perclosScore = Math.max(0, 100 - perclos * 500);   // 0% = 100, 20% = 0
  const poseScore    = Math.max(0, 100 - (Math.abs(headYaw) + Math.abs(headPitch)) * 120);
  const durationScore = blinkDurationAvg > 0
    ? Math.max(0, 100 - (blinkDurationAvg - 150) / 3)  // 150ms normal, longer = fatigue
    : 80;

  const score = (
    gazeScore    * 0.30 +
    perclosScore * 0.25 +
    blinkScore   * 0.20 +
    poseScore    * 0.15 +
    durationScore * 0.10
  );
  return Math.round(Math.max(0, Math.min(100, score)));
}

// ── Daily summary computation ────────────────────────────────────────────────
export function computeDailySummary(snapshots) {
  if (!snapshots.length) return null;

  const present = snapshots.filter(s => s.facePresent);
  const avgAttn = present.length ? Math.round(avg(present.map(s => s.attentionScore))) : 0;

  // Group by hour for peak/low detection
  const byHour = {};
  snapshots.forEach(s => {
    const h = new Date(s.timestamp).getHours();
    if (!byHour[h]) byHour[h] = [];
    byHour[h].push(s.attentionScore || 0);
  });
  const hourAvgs = Object.entries(byHour).map(([h, vals]) => ({ h: +h, avg: avg(vals) }));
  hourAvgs.sort((a,b) => b.avg - a.avg);

  // Focus streaks (consecutive high-attention snapshots)
  let maxStreak = 0, curStreak = 0;
  snapshots.forEach(s => {
    if ((s.attentionScore || 0) >= 60) { curStreak++; maxStreak = Math.max(maxStreak, curStreak); }
    else curStreak = 0;
  });

  // Emotion distribution
  const emotionCounts = {};
  snapshots.forEach(s => {
    if (s.emotion) emotionCounts[s.emotion] = (emotionCounts[s.emotion] || 0) + 1;
  });
  const total = snapshots.length;
  const emotionDistribution = Object.fromEntries(
    Object.entries(emotionCounts).map(([e, c]) => [e, Math.round(c / total * 100)])
  );

  return {
    avgAttentionScore:    avgAttn,
    peakFocusHour:        hourAvgs[0]?.h ?? null,
    lowestFocusHour:      hourAvgs[hourAvgs.length-1]?.h ?? null,
    longestFocusStreakMin: Math.round(maxStreak),  // each snapshot = 1 minute
    totalFacePresentMin:  present.length,
    avgBlinkDuration:     present.length ? Math.round(avg(present.map(s => s.blinkDurationAvg || 150))) : 0,
    avgPerclos:           present.length ? +(avg(present.map(s => s.perclos || 0))).toFixed(3) : 0,
    avgBlinkRate:         present.length ? +(avg(present.map(s => s.blinkRate || 0))).toFixed(1) : 0,
    emotionDistribution,
    yawnCount:            snapshots.reduce((s, snap) => s + (snap.yawns || 0), 0),
  };
}

// ── Extended metrics (new) ──────────────────────────────────────────────────

export function computeExtendedSummary(snapshots) {
  if (!snapshots.length) return null;
  const present = snapshots.filter(s => s.facePresent);
  if (!present.length) return null;

  const avg = arr => arr.reduce((a,b) => a+b, 0) / arr.length;

  // ── Session Quality Score (0–100) ─────────────────────────────────
  // Weighted: attention + low fatigue + emotion stability + face time
  const attnAvg    = avg(present.map(s => s.attentionScore || 0));
  const perclosAvg = avg(present.map(s => s.perclos || 0));
  const fatiguePenalty = Math.min(100, perclosAvg * 600); // 16% perclos = max penalty
  const facePct    = present.length / snapshots.length;
  const emotions   = present.map(s => s.emotion).filter(Boolean);
  const positiveEmotions = ['happy','calm','concentrating'];
  const positivePct = emotions.length ? emotions.filter(e => positiveEmotions.includes(e)).length / emotions.length : 0.5;
  const sessionQuality = Math.round(
    attnAvg * 0.45 +
    (100 - fatiguePenalty) * 0.25 +
    positivePct * 100 * 0.15 +
    facePct * 100 * 0.15
  );

  // ── Flow State (attention >= 75, positive/neutral emotion) ────────
  const flowSnapshots = present.filter(s =>
    (s.attentionScore || 0) >= 75 &&
    ['happy','calm','concentrating','neutral'].includes(s.emotion)
  );
  const flowMinutes = flowSnapshots.length; // 1 snapshot = 1 min
  // Longest continuous flow streak
  let maxFlow = 0, curFlow = 0;
  present.forEach(s => {
    const inFlow = (s.attentionScore || 0) >= 75 && ['happy','calm','concentrating','neutral'].includes(s.emotion);
    if (inFlow) { curFlow++; maxFlow = Math.max(maxFlow, curFlow); }
    else curFlow = 0;
  });

  // ── Distraction Frequency ─────────────────────────────────────────
  // Count drops: attention going from >=60 to <40
  let distractions = 0;
  for (let i = 1; i < present.length; i++) {
    if ((present[i-1].attentionScore || 0) >= 60 && (present[i].attentionScore || 0) < 40) distractions++;
  }

  // ── Recovery Time ─────────────────────────────────────────────────
  // After each dip below 40, how many snapshots (mins) to get back above 60
  const recoveryTimes = [];
  let inDip = false, dipStart = -1;
  for (let i = 0; i < present.length; i++) {
    const a = present[i].attentionScore || 0;
    if (!inDip && a < 40) { inDip = true; dipStart = i; }
    else if (inDip && a >= 60) { recoveryTimes.push(i - dipStart); inDip = false; }
  }
  const avgRecovery = recoveryTimes.length ? Math.round(avg(recoveryTimes)) : null;

  // ── Eye Strain Index (0–100) ──────────────────────────────────────
  // Low blink rate (<10/min sustained) = screen strain risk
  const blinkRates = present.map(s => s.blinkRate || 0).filter(r => r > 0);
  const lowBlinkMins = blinkRates.filter(r => r < 10).length;
  const eyeStrainIndex = blinkRates.length ? Math.round((lowBlinkMins / blinkRates.length) * 100) : 0;

  // ── Fatigue Onset ─────────────────────────────────────────────────
  const fatigueOnset = present.find(s => (s.perclos || 0) > 0.10);
  const fatigueOnsetTime = fatigueOnset
    ? new Date(fatigueOnset.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })
    : null;

  // ── Cognitive Load Score (0–100) ──────────────────────────────────
  // High blink variability + stressed/angry emotion + high perclos = high load
  const blinkVar = avg(present.map(s => s.blinkVariability || 0));
  const stressEmotions = ['stressed','angry','fearful','disgusted'];
  const stressPct = emotions.length ? emotions.filter(e => stressEmotions.includes(e)).length / emotions.length : 0;
  const cognitiveLoad = Math.min(100, Math.round(
    blinkVar * 0.5 +
    stressPct * 100 * 0.3 +
    perclosAvg * 200 * 0.2
  ));

  // ── Emotion Stability (0–100, higher = more stable) ──────────────
  let emotionChanges = 0;
  for (let i = 1; i < present.length; i++) {
    if (present[i].emotion && present[i-1].emotion && present[i].emotion !== present[i-1].emotion) emotionChanges++;
  }
  const emotionStability = present.length > 1
    ? Math.round(100 - (emotionChanges / (present.length - 1)) * 100)
    : 100;

  // ── Frustration Index (0–100) ─────────────────────────────────────
  // High head yaw + negative emotion + attention drops
  const highYawMins = present.filter(s => Math.abs(s.headYaw || 0) > 0.3).length;
  const frustrationIndex = Math.min(100, Math.round(
    (highYawMins / present.length) * 40 +
    stressPct * 40 +
    (distractions / Math.max(1, present.length)) * 200 * 20
  ));

  // ── Morning vs Afternoon ──────────────────────────────────────────
  const morning   = present.filter(s => new Date(s.timestamp).getHours() < 12);
  const afternoon = present.filter(s => new Date(s.timestamp).getHours() >= 12);
  const morningAttn   = morning.length   ? Math.round(avg(morning.map(s => s.attentionScore || 0)))   : null;
  const afternoonAttn = afternoon.length ? Math.round(avg(afternoon.map(s => s.attentionScore || 0))) : null;

  // ── Fatigue progression for chart ─────────────────────────────────
  const fatigueChart = snapshots.map(s => ({
    time: new Date(s.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }),
    perclos: +(( s.perclos || 0) * 100).toFixed(1),
    eyeStrain: (s.blinkRate || 0) < 10 ? 1 : 0,
  }));

  // ── Flow timeline for chart ────────────────────────────────────────
  const flowTimeline = present.map(s => ({
    time: new Date(s.timestamp).toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }),
    attention: s.attentionScore || 0,
    flow: (s.attentionScore || 0) >= 75 ? (s.attentionScore || 0) : null,
  }));

  return {
    sessionQuality,
    flowMinutes,
    longestFlowStreak: maxFlow,
    distractions,
    avgRecoveryMins: avgRecovery,
    eyeStrainIndex,
    fatigueOnsetTime,
    cognitiveLoad,
    emotionStability,
    frustrationIndex,
    morningAttn,
    afternoonAttn,
    fatigueChart,
    flowTimeline,
  };
}

// ── Personal baseline from historical daily summaries ────────────────────────
export function computePersonalBaseline(dailySummaries) {
  if (!dailySummaries || dailySummaries.length === 0) return null;

  const avg = arr => arr.filter(v => v != null && !isNaN(v)).reduce((a,b) => a+b, 0) /
    arr.filter(v => v != null && !isNaN(v)).length || null;

  const attentions  = dailySummaries.map(d => d.avgAttentionScore).filter(Boolean);
  const perclos     = dailySummaries.map(d => d.avgPerclos).filter(v => v != null);
  const blinkRates  = dailySummaries.map(d => d.avgBlinkRate).filter(Boolean);
  const yawns       = dailySummaries.map(d => d.yawnCount).filter(v => v != null);
  const flowMins    = dailySummaries.map(d => d.flowMinutes).filter(v => v != null);

  return {
    avgAttention:  avg(attentions)  ? Math.round(avg(attentions))        : null,
    avgPerclos:    avg(perclos)     ? +(avg(perclos)).toFixed(3)          : null,
    avgBlinkRate:  avg(blinkRates)  ? +(avg(blinkRates)).toFixed(1)      : null,
    avgYawns:      avg(yawns)       ? +(avg(yawns)).toFixed(1)           : null,
    avgFlowMins:   avg(flowMins)    ? Math.round(avg(flowMins))          : null,
    daysOfData:    dailySummaries.length,
  };
}
